import injectSheet, { createStyles } from 'core/style/injectSheet';
import { ITheme, WithStyles } from 'core/style/interfaces';
import { numberSeparateThousands } from 'core/utils';
import { formatToLocalDayMonthYearDot } from 'core/utils/date';
import { numEnding } from 'core/utils/string';
import React from 'react';
import { CurrencyEnum } from 'site/graphql';
import { IWeekInvestment } from './helpers';

type IService52WeeksInfoProps = {
    weekInvestments: IWeekInvestment[];
    currency: CurrencyEnum;
} & WithStyles<typeof styles>;

const getCurrencyText = (currency: CurrencyEnum, value: number) => {
    switch (currency) {
        case CurrencyEnum.Rub:
            return numEnding(value, ['рубль', 'рубля', 'рублей']);
        case CurrencyEnum.Usd:
            return numEnding(value, ['доллар', 'доллара', 'долларов']);
        default:
            return '';
    }
};

const Service52WeeksInfo: React.FC<IService52WeeksInfoProps> = ({ classes, weekInvestments, currency }) => {
    const currentWeek = weekInvestments.find(w => w.isCurrentWeek);

    // Текущая неделя не найдена
    if (!currentWeek) {
        const firstWeek = weekInvestments[0];

        // 2 случая: либо не началось
        if (firstWeek.fact.sum === null) {
            return (
                <p className={classes.text}>
                    На неделе с <span className={classes.redText}>{formatToLocalDayMonthYearDot(firstWeek.from)}</span>{' '}
                    по <span className={classes.redText}>{formatToLocalDayMonthYearDot(firstWeek.to)}</span>{' '}
                    <b>по плану</b> тебе нужно отложить{' '}
                    <span className={classes.redText}>{numberSeparateThousands(firstWeek.plan.investment)}</span>{' '}
                    {getCurrencyText(currency, firstWeek.plan.investment)}. Твои общие накопления составят{' '}
                    <span className={classes.redText}>{numberSeparateThousands(firstWeek.plan.sum)}</span>{' '}
                    {getCurrencyText(currency, firstWeek.plan.sum)}.
                </p>
            );
        } else {
            // либо закончилось
            return <p className={classes.text}>План накоплений подошел к концу.</p>;
        }
    }

    return (
        <p className={classes.text}>
            На неделе с <span className={classes.redText}>{formatToLocalDayMonthYearDot(currentWeek.from)}</span> по{' '}
            <span className={classes.redText}>{formatToLocalDayMonthYearDot(currentWeek.to)}</span> <b>по плану</b> тебе
            нужно отложить{' '}
            <span className={classes.redText}>
                {numberSeparateThousands(currentWeek.plan.investment)}{' '}
                {getCurrencyText(currency, currentWeek.plan.investment)}
            </span>
            . Твои общие накопления составят{' '}
            <span className={classes.redText}>
                {numberSeparateThousands(currentWeek.plan.sum)} {getCurrencyText(currency, currentWeek.plan.sum)}
            </span>
            . <b>По факту</b> ты отложил(-а){' '}
            <span className={classes.redText}>
                {numberSeparateThousands(currentWeek.fact.investment)}{' '}
                {getCurrencyText(currency, currentWeek.fact.investment)}
            </span>
            . При этом твои общие накопления составляют{' '}
            <span className={classes.redText}>
                {numberSeparateThousands(currentWeek.fact.sum)} {getCurrencyText(currency, currentWeek.fact.sum)}
            </span>
            .
        </p>
    );
};

const styles = (theme: ITheme) =>
    createStyles({
        text: { fontSize: 16 },
        redText: { color: theme.colors.accent },
    });

export default injectSheet(styles)(Service52WeeksInfo);
